import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TICKET_CATEGORIES, TICKET_CATEGORY_LABELS, type TicketCategory, type TicketDTO } from '@va/shared';
import { api, errorMessage } from '../api/client';
import { useArticle, useArticles, useConfig, useProducts, useTickets } from '../api/queries';
import { IconArrowLeft, IconChat, IconLifebuoy } from '../components/icons';
import { LogList, LogUploader, type PickedLog } from '../components/LogUploader';
import { Empty, ErrorBox, formatDate, Loading, Markdown, Spinner, timeAgo } from '../components/ui';
import { desktop, isDesktop } from '../platform';
import { useAuth } from '../state/auth';
import { useToast } from '../state/toast';

const STATUS_LABEL: Record<string, { label: string; tone: string }> = {
  open: { label: 'Waiting for us', tone: 'warning' },
  pending: { label: 'Replied', tone: 'success' },
  closed: { label: 'Closed', tone: 'muted' },
};

export function SupportPage() {
  const { user } = useAuth();
  const articles = useArticles();
  const tickets = useTickets();
  const config = useConfig();
  const [q, setQ] = useState('');

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (articles.data ?? []).filter((a) => !needle || a.title.toLowerCase().includes(needle) || a.body.toLowerCase().includes(needle));
    const map = new Map<string, typeof list>();
    for (const a of list) map.set(a.category, [...(map.get(a.category) ?? []), a]);
    return [...map.entries()];
  }, [articles.data, q]);

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <span className="eyebrow">Help center</span>
          <h1>How can we help?</h1>
        </div>
        <Link to="/support/new" className="btn btn-primary"><IconChat size={16} /> Contact support</Link>
      </div>

      <div className="split" style={{ gridTemplateColumns: 'minmax(0,1.6fr) minmax(280px,1fr)', alignItems: 'start' }}>
        <div className="stack lg">
          <input className="input" placeholder="Search the help center" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the help center" />
          {articles.isLoading && <Loading />}
          {articles.error && <ErrorBox error={articles.error} />}
          {grouped.map(([cat, list]) => (
            <div key={cat}>
              <span className="label">{cat}</span>
              <div className="card" style={{ padding: 0, marginTop: 8 }}>
                {list.map((a) => (
                  <Link key={a.id} to={`/support/articles/${a.slug}`} className="list-row" style={{ color: 'var(--va-headline)' }}>
                    <span className="grow">{a.title}</span>
                    <span className="dim">→</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
          {articles.data && grouped.length === 0 && <Empty title="No articles match">Try different words, or contact us.</Empty>}
        </div>

        <aside className="stack">
          <div className="card stack">
            <div className="row"><IconLifebuoy /> <h3 style={{ margin: 0 }}>Your tickets</h3></div>
            {!user && (
              <p className="small muted" style={{ margin: 0 }}>
                <Link to="/login" state={{ from: '/support' }}>Sign in</Link> to see your tickets. You can also write to us without an account.
              </p>
            )}
            {user && tickets.isLoading && <Spinner />}
            {user && tickets.data?.length === 0 && <p className="small muted" style={{ margin: 0 }}>No tickets yet.</p>}
            {tickets.data?.map((t) => (
              <Link key={t.id} to={`/support/tickets/${t.id}`} className="row between" style={{ padding: '8px 0', borderTop: '1px solid var(--va-line)', color: 'var(--va-body)' }}>
                <span>
                  <span className="mono dim">#{t.number}</span> {t.subject}
                  <br />
                  <span className="small dim">updated {timeAgo(t.updatedAt)}</span>
                </span>
                <span className={`pill ${STATUS_LABEL[t.status]?.tone}`}>{STATUS_LABEL[t.status]?.label}</span>
              </Link>
            ))}
          </div>
          <div className="card flat small muted">
            Prefer email? Write to <a href={`mailto:${config.data?.supportEmail ?? ''}`}>{config.data?.supportEmail}</a>. Include your order number if it's about a purchase.
          </div>
        </aside>
      </div>
    </div>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const { data, error, isLoading } = useArticle(slug);
  return (
    <div className="container" style={{ maxWidth: 820 }}>
      <Link to="/support" className="btn btn-ghost btn-sm" style={{ marginBottom: 16, paddingLeft: 8 }}><IconArrowLeft size={16} /> Help center</Link>
      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && (
        <article className="card" style={{ padding: 32 }}>
          <span className="eyebrow">{data.category}</span>
          <h1 style={{ fontSize: 32 }}>{data.title}</h1>
          <Markdown source={data.body} />
          <hr className="divider" />
          <p className="small muted" style={{ margin: 0 }}>
            Still stuck? <Link to="/support/new">Contact support</Link> and we'll help you out.
          </p>
        </article>
      )}
    </div>
  );
}

export function NewTicketPage() {
  const { user } = useAuth();
  const toast = useToast();
  const nav = useNavigate();
  const products = useProducts();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    subject: '',
    category: (params.get('category') as TicketCategory) || 'licensing',
    productId: params.get('product') ?? '',
    body: '',
    email: '',
    name: '',
    attachDiagnostics: isDesktop,
  });
  const [logs, setLogs] = useState<PickedLog[]>([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<TicketDTO | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const diagnostics = form.attachDiagnostics && desktop ? await desktop.system.diagnostics() : undefined;
      const t = await api.post<TicketDTO>('/support/tickets', {
        subject: form.subject,
        category: form.category,
        productId: form.productId || null,
        body: form.body,
        diagnostics,
        logs: logs.map(({ name, content }) => ({ name, content })),
        ...(user ? {} : { email: form.email, name: form.name || undefined }),
      });
      if (user) nav(`/support/tickets/${t.id}`);
      else setSent(t);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="container auth-wrap">
        <div className="card auth-card center">
          <h2>Thanks, we got it</h2>
          <p className="muted">Ticket #{sent.number} is open. We've emailed you a link to follow the conversation.</p>
          <Link to="/support" className="btn btn-primary">Back to help center</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 760 }}>
      <Link to="/support" className="btn btn-ghost btn-sm" style={{ marginBottom: 16, paddingLeft: 8 }}><IconArrowLeft size={16} /> Help center</Link>
      <form className="card stack" onSubmit={submit}>
        <div>
          <h2>Contact support</h2>
          <p className="muted small" style={{ margin: 0 }}>We usually reply within one business day.</p>
        </div>
        {!user && (
          <div className="split">
            <div className="field">
              <label htmlFor="em">Your email</label>
              <input id="em" className="input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="nm">Name</label>
              <input id="nm" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
          </div>
        )}
        <div className="split">
          <div className="field">
            <label htmlFor="cat">Topic</label>
            <select id="cat" className="select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as TicketCategory })}>
              {TICKET_CATEGORIES.map((c) => (
                <option key={c} value={c}>{TICKET_CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="prod">Product</label>
            <select id="prod" className="select" value={form.productId} onChange={(e) => setForm({ ...form, productId: e.target.value })}>
              <option value="">Not about a specific product</option>
              {products.data?.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="subj">Subject</label>
          <input id="subj" className="input" required minLength={3} maxLength={140} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="body">Message</label>
          <textarea id="body" className="textarea" required minLength={10} rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="What happened, what did you expect, and which Resolume version are you on?" />
        </div>
        <LogUploader value={logs} onChange={setLogs} />
        {isDesktop && (
          <label className="checkbox">
            <input type="checkbox" checked={form.attachDiagnostics} onChange={(e) => setForm({ ...form, attachDiagnostics: e.target.checked })} />
            <span>Attach diagnostics (app version, OS, license status, install folders). No passwords or license keys are included.</span>
          </label>
        )}
        <div className="row between">
          <span />
          <button className="btn btn-primary" disabled={busy}>{busy ? <Spinner /> : 'Send'}</button>
        </div>
      </form>
    </div>
  );
}

export function TicketPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const key = params.get('key') ?? undefined;
  const qc = useQueryClient();
  const toast = useToast();
  const [reply, setReply] = useState('');
  const [logs, setLogs] = useState<PickedLog[]>([]);
  const [busy, setBusy] = useState(false);
  const ticket = useQuery({
    queryKey: ['ticket', id, key],
    queryFn: () => api.get<TicketDTO>(`/support/tickets/${id}${key ? `?key=${encodeURIComponent(key)}` : ''}`),
    refetchInterval: 30_000,
  });
  const t = ticket.data;

  const send = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const updated = await api.post<TicketDTO>(`/support/tickets/${id}/messages`, { body: reply, key, logs: logs.map(({ name, content }) => ({ name, content })) });
      qc.setQueryData(['ticket', id, key], updated);
      setReply('');
      setLogs([]);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const close = async () => {
    const updated = await api.post<TicketDTO>(`/support/tickets/${id}/close`, { key });
    qc.setQueryData(['ticket', id, key], updated);
  };

  return (
    <div className="container" style={{ maxWidth: 820 }}>
      <Link to="/support" className="btn btn-ghost btn-sm" style={{ marginBottom: 16, paddingLeft: 8 }}><IconArrowLeft size={16} /> Help center</Link>
      {ticket.isLoading && <Loading />}
      {ticket.error && <ErrorBox error={ticket.error} />}
      {t && (
        <div className="stack lg">
          <div className="row between wrap">
            <div>
              <span className="eyebrow">Ticket #{t.number} · {TICKET_CATEGORY_LABELS[t.category]}{t.productName ? ` · ${t.productName}` : ''}</span>
              <h2 style={{ margin: 0 }}>{t.subject}</h2>
            </div>
            <span className={`pill ${STATUS_LABEL[t.status]?.tone}`}>{STATUS_LABEL[t.status]?.label}</span>
          </div>
          {t.messages?.map((m) => (
            <div key={m.id} className="card" style={{ borderColor: m.authorRole === 'staff' ? 'var(--va-indigo-light)' : undefined, background: m.authorRole === 'staff' ? 'rgba(43,46,159,0.18)' : undefined }}>
              <div className="row between small" style={{ marginBottom: 8 }}>
                <span style={{ color: 'var(--va-headline)', fontWeight: 600 }}>{m.authorRole === 'staff' ? `${m.authorName ?? 'VA Support'} · Visual Artform` : m.authorName ?? 'You'}</span>
                <span className="dim">{formatDate(m.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</span>
              </div>
              {m.body && <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>}
              <LogList files={m.attachments ?? []} />
            </div>
          ))}
          {t.status !== 'closed' ? (
            <form className="card stack" onSubmit={send}>
              <textarea className="textarea" placeholder="Write a reply" value={reply} onChange={(e) => setReply(e.target.value)} aria-label="Reply" />
              <LogUploader value={logs} onChange={setLogs} />
              <div className="row between">
                <button type="button" className="btn btn-ghost btn-sm" onClick={close}>Mark as solved</button>
                <button className="btn btn-primary" disabled={busy || (!reply.trim() && logs.length === 0)}>{busy ? <Spinner /> : 'Send reply'}</button>
              </div>
            </form>
          ) : (
            <div className="notice small">This ticket is closed. Replying isn't possible; <Link to="/support/new">open a new ticket</Link> if you need more help.</div>
          )}
        </div>
      )}
    </div>
  );
}
